import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import authService from '../../../Services/authService';

export default function Login() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const errors = {};
    const numberRegex = /^[6-9]\d{9}$/;

    if (!phoneNumber) {
      errors.phone_number = "Phone Number is Required";
    } else if (!numberRegex.test(phoneNumber)) {
      errors.phone_number = "Please Enter Valid Phone Number";
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsLoading(true);

      try {
        const response = await fetch(authService.getApiUrl() + 'send-otp', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify({
            phone_number: phoneNumber,
          }),
        });

        const result = await response.json();

        if (result.status !== 'failed') {
          setErrors({});
          navigate(`/otp-verification?phone=${encodeURIComponent(phoneNumber)}`);
        } else {
          setErrors({ phone_number: result.message });
        }
      } catch (err) {
        console.error("Error sending OTP:", err.message);
        setErrors({ phone_number: "Failed to send OTP. Please try again later." });
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log("Form submission failed due to validation errors.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E0E0E0] overflow-hidden">
            {/* Logo and Header */}
            <div className="p-6 border-b border-[#E0E0E0] flex flex-col items-center">
              <div className="mb-4 flex justify-center">
                <img src="/images/pranaam-365-logo.png" alt="Pranaam 365 Logo" className="h-12 w-auto" />
              </div>
              <h1 className="text-2xl font-semibold text-[#4A4A4A]">Login</h1>
              <p className="text-sm text-[#4A4A4A]/70 text-center mt-2">
                Enter your phone number to receive an OTP for login.
              </p>
            </div>

            {/* Login Form */}
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                {/* Phone Number Field */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">Phone Number</label>
                  <div className="relative">
                    {/* <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Phone size={18} className="text-[#4A4A4A]/50" />
                    </div> */}
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 pr-2 pointer-events-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      className="w-full pl-10 py-2.5 border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]"
                      placeholder="Enter your phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                    />

                  </div>
                  {errors.phone_number && (
                    <p className="text-sm text-red-600 mt-1">{errors.phone_number}</p>
                  )}
                </div>

                {/* Send OTP Button */}
                <button
                  type="submit"
                  className="w-full bg-[#951C25] text-white py-3 rounded-lg font-medium hover:bg-[#951C25]/90 transition-colors flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP"
                  )}
                </button>

                {/* Register Link */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-[#4A4A4A]/70">
                    Don't have an account?{" "}
                    <Link to="/register" className="text-[#951C25] font-medium hover:underline">
                      Register
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white py-4 border-t border-[#E0E0E0]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-[#4A4A4A]/70">© 2025 Pranaam365. All rights reserved.</p>
            <div className="flex space-x-4 mt-2 md:mt-0">
              <Link to="/#" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Privacy Policy
              </Link>
              <Link to="/#" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Terms of Service
              </Link>
              <Link to="/#" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Help Center
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
